'use client'

import './FeedbackForm.scss'

import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormState } from 'react-dom'

import { Form } from '@/src/entities/application/ui/Form'
import { ImperativeHandlers } from '@/src/entities/application/ui/Form/Form'
import { IBookingTime } from '@/src/entities/event/api/interfaces'
import { sendForm } from '@/src/features/send-application/model/actions'
import { SubmitButton } from '@/src/features/send-application/ui/SubmitButton'
import { Container } from '@/src/shared/ui/Container'
import { Title } from '@/src/shared/ui/Title'

export type IFeedbackForm = {
  className?: string
  landingId?: number | string | null
  locale?: string
  dates: IBookingTime
}

const FeedbackForm = ({ className, landingId, locale, dates }: IFeedbackForm) => {
  const t = useTranslations('FormFields')
  const [state, formAction] = useFormState(sendForm, {
    isSuccess: false,
    isError: false,
    errors: {},
  })
  const [isSendedForm, setSendedForm] = useState(false)
  const formRef = useRef<ImperativeHandlers>(null)

  const isAgreedError: boolean = useMemo(() => {
    return state.errors.agree?.length > 0
  }, [state.errors])

  // Computed value for errorsKeys
  const errorsKeys: string = useMemo(() => {
    return Object.keys(state.errors)
      .filter((error) => ['email', 'phone'].includes(error))
      .join(', ')
  }, [state.errors])

  useEffect(() => {
    if (state.isSuccess) {
      setSendedForm(true)
      resetForm()
    }

    if (state.isError) {
      setSendedForm(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isSuccess, state.isError])

  const resetForm = () => {
    if (formRef.current) {
      formRef.current.resetForm()
    }
  }

  return (
    <section
      id="feedback-form"
      className={cn('feedback-form', {
        [`${className}`]: !!className,
      })}
    >
      <Container size="xs">
        <div className="feedback-form__inner">
          {!isSendedForm ? (
            <div className="feedback-form__form">
              <Title className="feedback-form__title" align="center">
                {t('formTitle')}
              </Title>
              <div className="feedback-form__body">
                <Form
                  dates={dates}
                  onSubmit={formAction}
                  errors={state.errors}
                  ref={formRef}
                  landingId={landingId}
                  locale={locale}
                >
                  <SubmitButton />
                  <div className="feedback-form__error-section">
                    {!!Object.keys(state.errors).length && (
                      <p className="feedback-form__error-text">
                        Please fill the following fields:{' '}
                        <span className="capitalized">{errorsKeys}</span>
                      </p>
                    )}
                    {isAgreedError && (
                      <p className="feedback-form__error-text">
                        Ensure the <span>agree field</span> is accepted.
                      </p>
                    )}
                  </div>
                </Form>
              </div>
            </div>
          ) : (
            <div className="feedback-form__success">
              <h4 className="feedback-form__success-title">{t('formSuccess')}</h4>
              <div className="feedback-form__success-description">{t('formContactWithYou')}</div>
            </div>
          )}
        </div>
      </Container>
    </section>
  )
}

export default FeedbackForm
